import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DatePicker } from '@mui/x-date-pickers';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { createTheme, useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';

const bulkCommFallbackTheme = createTheme();
const defaultResolvedTokenStyles = {
  valid: `background:${bulkCommFallbackTheme.palette.success.dark};color:${bulkCommFallbackTheme.palette.getContrastText(
    bulkCommFallbackTheme.palette.success.dark
  )};border-radius:6px;padding:1px 4px;`,
  invalid: `background:${bulkCommFallbackTheme.palette.error.dark};color:${bulkCommFallbackTheme.palette.getContrastText(
    bulkCommFallbackTheme.palette.error.dark
  )};border-radius:6px;padding:1px 4px;`,
};

import useBulkCommunication from '../../hooks/useBulkCommunication';
import { useAuth } from '../../context/authContext';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const defaultStartDate = dayjs().startOf('day');
const defaultEndDate = dayjs().endOf('day');
const bulkCommunicationDraftKeyPrefix = 'bulkCommunicationDraft';

const escapeHtml = value => {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const buildValuesFromRecipient = (recipient, placeholders) => {
  const base = Object.fromEntries((placeholders || []).map(item => [item.key, item.example]));
  if (!recipient) {
    return base;
  }

  const nameParts = (recipient.customerName || '').split(' ').filter(Boolean);
  const startTime = recipient.startTime ? dayjs(recipient.startTime) : null;

  return {
    ...base,
    Kundenname: recipient.customerName || base.Kundenname,
    Vorname: nameParts[0] || base.Vorname,
    Nachname: nameParts.slice(1).join(' ') || base.Nachname,
    Startdatum: startTime?.format('DD.MM.YYYY') || base.Startdatum,
    Startzeit: startTime?.format('HH:mm') || base.Startzeit,
    Buchungsnummer: recipient.bookingNumber || base.Buchungsnummer,
    Entertainment: recipient.entertainment || base.Entertainment,
  };
};

const resolveText = (template, values) =>
  String(template || '').replace(/\$([A-Za-zÄÖÜäöüß]+)/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match
  );


const buildResolvedHtml = (value, exampleValues, tokenStyles) => {
  const validStyle = tokenStyles?.valid || defaultResolvedTokenStyles.valid;
  const invalidStyle = tokenStyles?.invalid || defaultResolvedTokenStyles.invalid;

  const text = String(value || '');
  const regex = /\$[A-Za-zÄÖÜäöüß]+/g;
  let lastIndex = 0;
  let html = '';
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      html += escapeHtml(text.slice(lastIndex, match.index));
    }

    const key = match[0].replace(/^\$/, '');
    const isValid = Object.prototype.hasOwnProperty.call(exampleValues, key);
    const displayValue = isValid ? exampleValues[key] || match[0] : match[0];
    const style = isValid ? validStyle : invalidStyle;

    html += `<span style="${style}">${escapeHtml(displayValue)}</span>`;
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    html += escapeHtml(text.slice(lastIndex));
  }

  if (!html) {
    return '&nbsp;';
  }

  return html.replace(/\r?\n/g, '<br />') + '&nbsp;';
};

function TabPanel({ children, value, index }) {
  return (
    <Box role='tabpanel' hidden={value !== index}>
      {children}
    </Box>
  );
}

function TokenizedTextarea({
  label,
  inputRef,
  onFocus,
  onInputExtra,
  exampleValues,
  isExamplePreview = true,
  minHeight = 180,
  helperText,
}) {
  const theme = useTheme();
  const placeholderTokenStyles = useMemo(
    () => ({
      valid: `background:${theme.palette.success.dark};color:${theme.palette.getContrastText(
        theme.palette.success.dark
      )};border-radius:6px;padding:1px 4px;`,
      invalid: `background:${theme.palette.error.dark};color:${theme.palette.getContrastText(
        theme.palette.error.dark
      )};border-radius:6px;padding:1px 4px;`,
    }),
    [theme]
  );

  const previewRef = useRef(null);

  const syncHighlight = useCallback(() => {
    if (!previewRef.current) {
      return;
    }

    previewRef.current.innerHTML = buildResolvedHtml(
      inputRef.current?.value || '',
      exampleValues,
      placeholderTokenStyles
    );
  }, [inputRef, exampleValues, placeholderTokenStyles]);

  const handleInput = useCallback(() => {
    syncHighlight();
    if (onInputExtra) {
      onInputExtra();
    }
  }, [onInputExtra, syncHighlight]);

  useEffect(() => {
    syncHighlight();
  }, [syncHighlight]);

  return (
    <Stack spacing={1}>
      <Typography variant='subtitle2'>{label}</Typography>
      <Box
        component='textarea'
        ref={inputRef}
        onInput={handleInput}
        onFocus={onFocus}
        spellCheck
        sx={{
          width: '100%',
          minHeight,
          p: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          outline: 'none',
          resize: 'vertical',
          fontFamily: 'inherit',
          fontSize: '1rem',
          lineHeight: 1.5,
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxSizing: 'border-box',
          '&:focus': {
            borderColor: 'primary.main',
            boxShadow: theme => `0 0 0 1px ${theme.palette.primary.main}`,
          },
        }}
      />
      <Stack direction='row' alignItems='center' spacing={1}>
        <Typography variant='caption' color='text.disabled'>
          Vorschau
        </Typography>
        <Typography variant='caption' color={isExamplePreview ? 'text.disabled' : 'success.main'}>
          {isExamplePreview ? '(Beispielwerte)' : '(1. Empfänger)'}
        </Typography>
      </Stack>
      <Box
        ref={previewRef}
        aria-label='Platzhalter-Vorschau'
        sx={{
          p: 1.25,
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.default',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontFamily: 'inherit',
          fontSize: '0.875rem',
          lineHeight: 1.5,
          color: 'text.secondary',
          minHeight: 36,
        }}
      />
      {helperText ? (
        <Typography variant='caption' color='text.secondary'>
          {helperText}
        </Typography>
      ) : null}
    </Stack>
  );
}

export default function BulkCommunicationView() {
  const { getMeta, previewRecipients, sendBulkMail, sendBulkSms } = useBulkCommunication();
  const { currentUser } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [entertainments, setEntertainments] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [selectedEntertainmentId, setSelectedEntertainmentId] = useState('');
  const [fromDate, setFromDate] = useState(defaultStartDate);
  const [toDate, setToDate] = useState(defaultEndDate);
  const [preview, setPreview] = useState(null);
  const [isLoadingMeta, setIsLoadingMeta] = useState(true);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSendingMail, setIsSendingMail] = useState(false);
  const [isLegendOpen, setIsLegendOpen] = useState(false);
  const [isSendingSms, setIsSendingSms] = useState(false);
  const [activeEditor, setActiveEditor] = useState('mailBody');
  const [expandedPreviewId, setExpandedPreviewId] = useState(null);
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [sendConfirmType, setSendConfirmType] = useState(null);
  const [hasMailSubject, setHasMailSubject] = useState(false);
  const [hasMailBody, setHasMailBody] = useState(false);
  const [hasSmsBody, setHasSmsBody] = useState(false);
  const mailSubjectRef = useRef(null);
  const mailBodyRef = useRef(null);
  const smsBodyRef = useRef(null);
  const restoredDraftRef = useRef(false);

  const exampleValues = useMemo(
    () => buildValuesFromRecipient(preview?.recipients?.[0], placeholders),
    [placeholders, preview]
  );
  const draftStorageKey = useMemo(() => {
    const userId = currentUser?.userId || 'anonymous';
    return `${bulkCommunicationDraftKeyPrefix}:${userId}`;
  }, [currentUser?.userId]);

  const syncInputHighlight = useCallback(ref => {
    if (!ref?.current) {
      return;
    }

    ref.current.dispatchEvent(new Event('input', { bubbles: true }));
  }, []);

  const saveDraft = useCallback(
    overrides => {
      const draft = {
        tabValue,
        selectedEntertainmentId,
        fromDate: fromDate?.format('YYYY-MM-DD') || defaultStartDate.format('YYYY-MM-DD'),
        toDate: toDate?.format('YYYY-MM-DD') || defaultEndDate.format('YYYY-MM-DD'),
        mailSubject: mailSubjectRef.current?.value || '',
        mailBody: mailBodyRef.current?.value || '',
        smsBody: smsBodyRef.current?.value || '',
        ...overrides,
      };

      setHasMailSubject(!!draft.mailSubject.trim());
      setHasMailBody(!!draft.mailBody.trim());
      setHasSmsBody(!!draft.smsBody.trim());

      localStorage.setItem(draftStorageKey, JSON.stringify(draft));
    },
    [draftStorageKey, fromDate, selectedEntertainmentId, tabValue, toDate]
  );

  const resetDraft = useCallback(() => {
    localStorage.removeItem(draftStorageKey);
    restoredDraftRef.current = true;
    setTabValue(0);
    setFromDate(defaultStartDate);
    setToDate(defaultEndDate);
    setPreview(null);

    if (mailSubjectRef.current) {
      mailSubjectRef.current.value = '';
    }

    if (mailBodyRef.current) {
      mailBodyRef.current.value = '';
      syncInputHighlight(mailBodyRef);
    }

    if (smsBodyRef.current) {
      smsBodyRef.current.value = '';
      syncInputHighlight(smsBodyRef);
    }

    setSelectedEntertainmentId(entertainments[0]?._id || '');
    setHasMailSubject(false);
    setHasMailBody(false);
    setHasSmsBody(false);
  }, [draftStorageKey, entertainments, syncInputHighlight]);

  useEffect(() => {
    if (restoredDraftRef.current) {
      return;
    }

    let storedDraft = null;
    try {
      storedDraft = JSON.parse(localStorage.getItem(draftStorageKey) || 'null');
    } catch (error) {
      localStorage.removeItem(draftStorageKey);
    }

    if (!storedDraft) {
      restoredDraftRef.current = true;
      return;
    }

    if (typeof storedDraft.tabValue === 'number') {
      setTabValue(storedDraft.tabValue);
    }

    if (typeof storedDraft.selectedEntertainmentId === 'string') {
      setSelectedEntertainmentId(storedDraft.selectedEntertainmentId);
    }

    if (storedDraft.fromDate && dayjs(storedDraft.fromDate).isValid()) {
      setFromDate(dayjs(storedDraft.fromDate));
    }

    if (storedDraft.toDate && dayjs(storedDraft.toDate).isValid()) {
      setToDate(dayjs(storedDraft.toDate));
    }

    if (mailSubjectRef.current) {
      mailSubjectRef.current.value = storedDraft.mailSubject || '';
    }

    if (mailBodyRef.current) {
      mailBodyRef.current.value = storedDraft.mailBody || '';
      syncInputHighlight(mailBodyRef);
    }

    if (smsBodyRef.current) {
      smsBodyRef.current.value = storedDraft.smsBody || '';
      syncInputHighlight(smsBodyRef);
    }

    setHasMailSubject(!!(storedDraft.mailSubject || '').trim());
    setHasMailBody(!!(storedDraft.mailBody || '').trim());
    setHasSmsBody(!!(storedDraft.smsBody || '').trim());

    restoredDraftRef.current = true;
  }, [draftStorageKey, syncInputHighlight]);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        setIsLoadingMeta(true);
        const response = await getMeta();
        setEntertainments(response?.entertainments || []);
        setPlaceholders(response?.placeholders || []);
        if (
          !selectedEntertainmentId &&
          response?.entertainments?.length > 0
        ) {
          setSelectedEntertainmentId(response.entertainments[0]._id);
        }
      } catch (error) {
        toast.error('Fehler beim Laden der Massenversand-Daten');
      } finally {
        setIsLoadingMeta(false);
      }
    };

    loadMeta();
  }, [getMeta]);

  useEffect(() => {
    if (!restoredDraftRef.current) {
      return;
    }

    saveDraft();
  }, [tabValue, selectedEntertainmentId, fromDate, toDate, saveDraft]);

  const buildPayload = useCallback(() => {
    if (!selectedEntertainmentId || !fromDate || !toDate) {
      return null;
    }

    return {
      entertainmentId: selectedEntertainmentId,
      fromDate: fromDate.format('YYYY-MM-DD'),
      toDate: toDate.format('YYYY-MM-DD'),
    };
  }, [selectedEntertainmentId, fromDate, toDate]);

  const handlePreview = useCallback(async () => {
    const payload = buildPayload();
    if (!payload) {
      toast.info('Bitte zuerst Entertainment und Zeitraum auswählen.');
      return;
    }

    setIsPreviewing(true);
    try {
      const response = await previewRecipients(payload);
      setPreview(response);
      toast.success('Empfänger wurden aktualisiert.');
    } catch (error) {
      toast.error('Fehler beim Laden der Empfänger-Vorschau');
    } finally {
      setIsPreviewing(false);
    }
  }, [buildPayload, previewRecipients]);

  const insertPlaceholder = useCallback(
    token => {
      const currentTabIsMail = tabValue === 0;
      let targetRef = null;

      if (activeEditor === 'mailSubject') {
        targetRef = mailSubjectRef;
      } else if (activeEditor === 'mailBody') {
        targetRef = mailBodyRef;
      } else if (activeEditor === 'smsBody') {
        targetRef = smsBodyRef;
      } else {
        targetRef = currentTabIsMail ? mailBodyRef : smsBodyRef;
      }

      const input = targetRef?.current;
      if (!input) {
        return;
      }

      const selectionStart = input.selectionStart ?? input.value.length;
      const selectionEnd = input.selectionEnd ?? input.value.length;
      const nextValue =
        input.value.slice(0, selectionStart) + token + input.value.slice(selectionEnd);

      input.value = nextValue;
      input.focus();

      const nextCursorPosition = selectionStart + token.length;
      if (typeof input.setSelectionRange === 'function') {
        input.setSelectionRange(nextCursorPosition, nextCursorPosition);
      }

      input.dispatchEvent(new Event('input', { bubbles: true }));
      saveDraft();
    },
    [activeEditor, saveDraft, tabValue]
  );

  const handleSendMail = useCallback(async () => {
    const payload = buildPayload();
    const subject = mailSubjectRef.current?.value?.trim() || '';
    const bodyText = mailBodyRef.current?.value?.trim() || '';

    if (!payload) {
      toast.info('Bitte zuerst Entertainment und Zeitraum auswählen.');
      return;
    }

    if (!subject || !bodyText) {
      toast.info('Bitte Betreff und Nachricht für den Mailversand ausfüllen.');
      return;
    }

    setIsSendingMail(true);
    try {
      const response = await sendBulkMail({
        ...payload,
        subject,
        bodyText,
      });
      toast.success(`${response?.queuedCount || 0} E-Mails wurden eingeplant.`);
      await handlePreview();
    } catch (error) {
      toast.error('Fehler beim Einplanen des Mailversands');
    } finally {
      setIsSendingMail(false);
    }
  }, [buildPayload, handlePreview, sendBulkMail]);

  const handleSendSms = useCallback(async () => {
    const payload = buildPayload();
    const bodyText = smsBodyRef.current?.value?.trim() || '';

    if (!payload) {
      toast.info('Bitte zuerst Entertainment und Zeitraum auswählen.');
      return;
    }

    if (!bodyText) {
      toast.info('Bitte einen Nachrichtentext für den SMS-Versand eingeben.');
      return;
    }

    setIsSendingSms(true);
    try {
      const response = await sendBulkSms({
        ...payload,
        bodyText,
      });
      toast.success(`${response?.queuedCount || 0} SMS wurden eingeplant.`);
      await handlePreview();
    } catch (error) {
      toast.error('Fehler beim Einplanen des SMS-Versands');
    } finally {
      setIsSendingSms(false);
    }
  }, [buildPayload, handlePreview, sendBulkSms]);

  return (
    <Box sx={viewRootTextPrimarySx}>
      <Box sx={{ p: 3, ...viewRootTextPrimarySx }}>
      <Stack spacing={3}>
        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant='h5'>Massenversand</Typography>

            {isLoadingMeta ? (
              <Box sx={{ ...viewRootTextPrimarySx, display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
                  <FormControl sx={{ flex: '2 1 200px', minWidth: 160 }}>
                    <InputLabel id='bulk-communication-entertainment-label'>Entertainment</InputLabel>
                    <Select
                      labelId='bulk-communication-entertainment-label'
                      label='Entertainment'
                      value={selectedEntertainmentId}
                      onChange={event => setSelectedEntertainmentId(event.target.value)}
                    >
                      {entertainments.map(entertainment => (
                        <MenuItem key={entertainment._id} value={entertainment._id}>
                          {entertainment.longName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <DatePicker
                    label='Von'
                    value={fromDate}
                    onChange={newValue => setFromDate(newValue || defaultStartDate)}
                    slotProps={{
                      textField: {
                        sx: { flex: '1 1 140px', minWidth: 130 },
                      },
                    }}
                  />

                  <DatePicker
                    label='Bis'
                    value={toDate}
                    onChange={newValue => setToDate(newValue || defaultEndDate)}
                    slotProps={{
                      textField: {
                        sx: { flex: '1 1 140px', minWidth: 130 },
                      },
                    }}
                  />
                </Box>

                <Stack direction='row' spacing={1.5} flexWrap='wrap' useFlexGap>
                  <Button
                    variant='contained'
                    onClick={handlePreview}
                    disabled={isPreviewing || isLoadingMeta}
                    sx={{ alignSelf: 'flex-start', minWidth: 200 }}
                  >
                    {isPreviewing ? <CircularProgress size={20} /> : 'Empfänger prüfen'}
                  </Button>

                  <Button
                    variant='outlined'
                    color='warning'
                    onClick={() => setIsResetConfirmOpen(true)}
                    disabled={isLoadingMeta}
                    sx={{ alignSelf: 'flex-start', minWidth: 200 }}
                  >
                    Entwurf zurücksetzen
                  </Button>
                </Stack>
              </>
            )}

            <Divider />

            {preview ? (
              <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap'>
                <Chip label={`${preview.summary.totalRecipients} Gesamt`} color='primary' />
                <Chip label={`${preview.summary.emailRecipients} E-Mail`} color='success' />
                <Chip label={`${preview.summary.smsRecipients} SMS`} color='secondary' />
                <Chip label={`${preview.summary.missingMail} ohne E-Mail`} />
                <Chip label={`${preview.summary.missingPhone} ohne Mobilnummer`} />
              </Stack>
            ) : (
              <Alert severity='info'>
                Noch keine Empfängervorschau geladen. Prüfe zuerst das Entertainment und den
                Zeitraum.
              </Alert>
            )}
          </Stack>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              sx={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => setIsLegendOpen(prev => !prev)}
            >
              <Typography variant='h6'>Platzhalter</Typography>
              <IconButton size='small' aria-label={isLegendOpen ? 'Legende einklappen' : 'Legende ausklappen'}>
                <ExpandMoreIcon
                  sx={{
                    transform: isLegendOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                  }}
                />
              </IconButton>
            </Stack>

            {!isLegendOpen && (
              <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap'>
                {placeholders
                  .filter(item => ['Kundenname', 'Startdatum', 'Startzeit', 'Buchungsnummer'].includes(item.key))
                  .map(item => (
                    <Chip
                      key={item.key}
                      label={`${item.label} · ${item.description}`}
                      size='small'
                      color='info'
                      variant='outlined'
                      onClick={event => {
                        event.stopPropagation();
                        insertPlaceholder(item.label);
                      }}
                      clickable
                    />
                  ))}
              </Stack>
            )}

            <Collapse in={isLegendOpen}>
              <Stack spacing={1.5} mt={1}>
                <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap'>
                  {placeholders.map(item => (
                    <Chip
                      key={item.key}
                      label={`${item.label} · ${item.description}`}
                      onClick={() => insertPlaceholder(item.label)}
                      clickable
                      color='info'
                      variant='outlined'
                    />
                  ))}
                </Stack>
              </Stack>
            </Collapse>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label='E-Mail' />
            <Tab label='SMS' />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <Stack spacing={2} mt={2}>
              <TextField
                label='Betreff'
                inputRef={mailSubjectRef}
                defaultValue=''
                onInput={() => saveDraft()}
                onFocus={() => setActiveEditor('mailSubject')}
                helperText='Platzhalter funktionieren auch im Betreff.'
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              <TokenizedTextarea
                label='Mailtext'
                inputRef={mailBodyRef}
                onFocus={() => setActiveEditor('mailBody')}
                onInputExtra={saveDraft}
                exampleValues={exampleValues}
                isExamplePreview={!preview?.recipients?.length}
                minHeight={220}
              />

              <Button
                variant='contained'
                onClick={() => setSendConfirmType('mail')}
                disabled={isSendingMail || isLoadingMeta || !preview?.summary?.emailRecipients || !hasMailSubject || !hasMailBody}
                sx={{ alignSelf: 'flex-start', minWidth: 220 }}
              >
                {isSendingMail ? <CircularProgress size={20} /> : 'E-Mails anstoßen'}
              </Button>
            </Stack>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Stack spacing={2} mt={2}>
              <TokenizedTextarea
                label='SMS-Text'
                inputRef={smsBodyRef}
                onFocus={() => setActiveEditor('smsBody')}
                onInputExtra={saveDraft}
                exampleValues={exampleValues}
                isExamplePreview={!preview?.recipients?.length}
                helperText='Der SMS-Text wird pro Empfänger personalisiert.'
                minHeight={180}
              />

              <Button
                variant='contained'
                onClick={() => setSendConfirmType('sms')}
                disabled={isSendingSms || isLoadingMeta || !preview?.summary?.smsRecipients || !hasSmsBody}
                sx={{ alignSelf: 'flex-start', minWidth: 220 }}
              >
                {isSendingSms ? <CircularProgress size={20} /> : 'SMS anstoßen'}
              </Button>
            </Stack>
          </TabPanel>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant='h6'>Empfänger-Beispiele</Typography>
            {preview?.sampleRecipients?.length ? (
              <List dense disablePadding>
                {preview.sampleRecipients.map(item => {
                  const recipientId = `${item.source}-${item.referenceId}`;
                  const isExpanded = expandedPreviewId === recipientId;

                  return (
                    <Box key={recipientId}>
                      <ListItem
                        divider
                        secondaryAction={
                          <Stack direction='row' spacing={1} alignItems='center'>
                            {item.hasMail ? <Chip size='small' color='success' label='Mail' /> : null}
                            {item.hasSms ? <Chip size='small' color='secondary' label='SMS' /> : null}
                            <IconButton
                              size='small'
                              onClick={() =>
                                setExpandedPreviewId(prev => (prev === recipientId ? null : recipientId))
                              }
                              aria-label={isExpanded ? 'Vorschau einklappen' : 'Vorschau anzeigen'}
                            >
                              <ExpandMoreIcon
                                sx={{
                                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.2s',
                                }}
                              />
                            </IconButton>
                          </Stack>
                        }
                      >
                        <ListItemText
                          primary={`${item.customerName || 'Unbekannt'} · ${item.entertainment || '—'} · ${item.bookingNumber || '—'}`}
                          secondary={`${dayjs(item.startTime).format('DD.MM.YYYY HH:mm')} · ${item.mail || 'keine E-Mail'} · ${item.phone || 'keine Nummer'}`}
                        />
                      </ListItem>

                      <Collapse in={isExpanded}>
                        {(() => {
                          const values = buildValuesFromRecipient(item, placeholders);
                          const subject = mailSubjectRef.current?.value || '';
                          const mailBody = mailBodyRef.current?.value || '';
                          const smsBody = smsBodyRef.current?.value || '';
                          const hasMailContent = item.hasMail && (subject || mailBody);
                          const hasSmsContent = item.hasSms && smsBody;

                          return (
                            <Box
                              sx={{
                                px: 2,
                                py: 1.5,
                                bgcolor: 'background.default',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              <Stack spacing={2}>
                                {hasMailContent ? (
                                  <Stack spacing={1}>
                                    <Typography variant='caption' color='text.disabled' fontWeight={600}>
                                      E-Mail Vorschau
                                    </Typography>
                                    {subject ? (
                                      <Box>
                                        <Typography variant='caption' color='text.secondary'>
                                          Betreff
                                        </Typography>
                                        <Typography variant='body2'>
                                          {resolveText(subject, values)}
                                        </Typography>
                                      </Box>
                                    ) : null}
                                    {mailBody ? (
                                      <Box>
                                        <Typography variant='caption' color='text.secondary'>
                                          Nachricht
                                        </Typography>
                                        <Typography
                                          variant='body2'
                                          sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}
                                        >
                                          {resolveText(mailBody, values)}
                                        </Typography>
                                      </Box>
                                    ) : null}
                                  </Stack>
                                ) : null}

                                {hasSmsContent ? (
                                  <Stack spacing={1}>
                                    <Typography variant='caption' color='text.disabled' fontWeight={600}>
                                      SMS Vorschau
                                    </Typography>
                                    <Typography
                                      variant='body2'
                                      sx={{ whiteSpace: 'pre-wrap' }}
                                    >
                                      {resolveText(smsBody, values)}
                                    </Typography>
                                  </Stack>
                                ) : null}

                                {!hasMailContent && !hasSmsContent ? (
                                  <Typography variant='body2' color='text.disabled'>
                                    Noch kein Text eingegeben.
                                  </Typography>
                                ) : null}
                              </Stack>
                            </Box>
                          );
                        })()}
                      </Collapse>
                    </Box>
                  );
                })}
              </List>
            ) : (
              <Typography variant='body2' color='text.secondary'>
                Noch keine Beispiel-Empfänger vorhanden.
              </Typography>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Box>

    <Dialog open={!!sendConfirmType} onClose={() => setSendConfirmType(null)}>
      <DialogTitle>
        {sendConfirmType === 'mail' ? 'E-Mail-Versand bestätigen' : 'SMS-Versand bestätigen'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {sendConfirmType === 'mail'
            ? `Es werden ${preview?.summary?.emailRecipients ?? '?'} E-Mails an die ermittelten Empfänger versendet. Dieser Vorgang kann nicht rückgängig gemacht werden.`
            : `Es werden ${preview?.summary?.smsRecipients ?? '?'} SMS an die ermittelten Empfänger versendet. Dieser Vorgang kann nicht rückgängig gemacht werden.`}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setSendConfirmType(null)}>Abbrechen</Button>
        <Button
          variant='contained'
          color='error'
          onClick={() => {
            const type = sendConfirmType;
            setSendConfirmType(null);
            if (type === 'mail') {
              handleSendMail();
            } else {
              handleSendSms();
            }
          }}
        >
          {sendConfirmType === 'mail' ? 'E-Mails jetzt senden' : 'SMS jetzt senden'}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={isResetConfirmOpen} onClose={() => setIsResetConfirmOpen(false)}>
      <DialogTitle>Entwurf zurücksetzen?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Alle eingegebenen Texte, Filter und der aktuelle Tab werden gelöscht. Diese Aktion kann
          nicht rückgängig gemacht werden.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setIsResetConfirmOpen(false)}>Abbrechen</Button>
        <Button
          color='warning'
          variant='contained'
          onClick={() => {
            setIsResetConfirmOpen(false);
            resetDraft();
          }}
        >
          Zurücksetzen
        </Button>
      </DialogActions>
    </Dialog>
    </Box>
  );
}
